import React, { useState, useEffect } from 'react'
import { FaTrash } from 'react-icons/fa'
import { Stack, Wrap, WrapItem, Box, Tag, Button, Heading, Text, Input, Textarea, SimpleModal, SimpleTable, EmptyBox, ConfirmButton, PermissionChooser, BottomStickyBar } from '@/components/lib'
import api from '@/api'
import useStore from '@/store'
import utils from '@/utils/utils'
import useTitle from '@/hooks/useTitle'

export default function Roles () {
  const [manageRole, setManageRole] = useState(null)

  const { trial, getRolesForTrial, addRole, removeRole, sites, groups } = useStore()
  useTitle('Roles', trial)
  const trialId = trial?._id
  const roles = getRolesForTrial(trialId)

  useEffect(() => {
    api.roles.getForTrial(trialId, d => d.roles?.forEach(addRole))
  }, [trialId])

  const onUpdateRole = (roleId, data) => {
    api.roles.update(roleId, data, (updatedRole) => {
      addRole(updatedRole)
      setManageRole(null)
    }, (err) => {
      utils.toast('error', 'There was an error updating the role', err.message)
    })
  }

  const createRole = () => {
    api.roles.create(trialId, { name: 'New role', description: '', permissions: {} }, (newRole) => {
      addRole(newRole)
      setManageRole(newRole)
    }, (err) => {
      console.error('Error creating role:', err)
      utils.toast('error', 'There was an error creating the role', err.message)
    })
  }
  const deleteRole = (role) => {
    api.roles.delete(role._id, () => {
      removeRole(role._id)
      setManageRole(null)
    })
  }

  return (
    <Stack gap={5}>
      <Stack direction={{ base: 'column', sm: 'row' }} justifyContent='space-between'>
        <Box>
          <Heading size='md'>Manage trial roles</Heading>
          <Text>Roles are permission sets that can be assigned to personnel.</Text>
        </Box>
        <Button colorPalette='primary' variant='solid' onClick={createRole}>Add a role for this trial</Button>
      </Stack>

      {roles?.length === 0
        ? (
          <EmptyBox icon='checklist' title='No roles added yet' description='Create a role by defining what permissions it should have.' />)
        : (
          <SimpleTable
            asCard
            tableProps={{ size: 'sm' }}
            headers={['Name', 'Description', 'Permissions', '']}
            rows={roles?.map(r => [
              r.name,
              r.description || <Text key={r._id} color='fg.subtle'>No description</Text>,
              <Wrap gap={1} key={r._id}>
                {r.permissions && r.permissions[trial._id]?.map(p => {
                  return <WrapItem key={p}><Tag key={p} colorPalette='blue' size='sm'>{p}</Tag></WrapItem>
                })}
                {groups?.map(g => {
                  const count = (r.permissions && r.permissions[g._id]?.length) || 0
                  if (count > 0) return <WrapItem key={g._id}><Tag key={g._id} size='sm'>{g.name} ({count} permissions)</Tag></WrapItem>
                  return null
                })}
                {sites?.map(s => {
                  const count = (r.permissions && r.permissions[s._id]?.length) || 0
                  if (count > 0) return <WrapItem key={s._id}><Tag key={s._id} size='sm'>{s.name} ({count} permissions)</Tag></WrapItem>
                  return null
                })}
              </Wrap>,
              <Stack direction='row' justifyContent='end' key={r._id}>
                <>
                  <Button colorPalette='primary' size='sm' variant='outline' onClick={() => setManageRole(r)}>Edit</Button>
                  <ConfirmButton
                    trigger={<Button variant='ghost' size='sm'><FaTrash /></Button>}
                    header='Really delete this role?'
                    footer='Users assigned to this role may lose some permissions.'
                    onConfirm={() => deleteRole(r)}
                  />
                </>
              </Stack>
            ])}
          />)}
      <RoleEditor role={manageRole} onClose={() => setManageRole(null)} onUpdateRole={onUpdateRole} groups={groups} sites={sites} />
    </Stack>
  )
}

function RoleEditor ({ role, onClose, onUpdateRole, groups, sites }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [permissions, setPermissions] = useState({})
  const { trial } = useStore()

  useEffect(() => {
    if (role) {
      setName(role.name || '')
      setDescription(role.description || '')
      setPermissions(role.permissions || {})
    }
  }, [role])

  const saveRole = () => {
    onUpdateRole(role._id, {
      name,
      description,
      permissions
    })
  }

  const onPermissionScopedChange = (scope, newPermissions) => {
    setPermissions({
      ...permissions,
      [scope]: newPermissions
    })
  }

  if (!role) return null

  return (
    <SimpleModal
      isOpen={!!role} onClose={onClose} size='lg'
      header='Manage role'
      body={
        <Stack gap={5}>
          <Stack gap={2}>
            <Heading size='sm'>About this role</Heading>
            <Input
              placeholder='Role name' value={name}
              onChange={e => setName(e.target.value)}
            />
            <Textarea placeholder='Role description' value={description} onChange={e => setDescription(e.target.value)} />
          </Stack>

          <Box p={3} bg='bg.subtle'>
            <PermissionChooser trial={trial} groups={groups} sites={sites} permissions={permissions} onScopedChange={onPermissionScopedChange} />
          </Box>

          <BottomStickyBar justifyContent='end'>
            <Button variant='ghost' onClick={onClose}>Cancel</Button>
            <Button onClick={saveRole} colorPalette='primary' variant='solid'>Save role</Button>
          </BottomStickyBar>
        </Stack>
      }
    />
  )
}
